import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useFormContext} from "react-hook-form";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBook, faQuestionCircle, faStickyNote} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Hooks
import useApi from "../hooks/useApi.js";
import useScrollListener from "../hooks/useScrollListener.js";
import useMountedState from "../hooks/useMountedState.js";

// Contexts
import {SettingsContext} from "../contexts/settings.js";

// Utils
import {COMPONENT, GROUP, FIELD, DEFAULT_DROPDOWN_PLACEHOLDER} from "../utils/builder.js";
import {exists, inViewport} from "../utils/helpers.js";

// Components
import CalloutMenu from "./CalloutMenu.js";
import AddNoteCalloutContent from "../pages/tasks/AddNoteCalloutContent.js";
import ViewNotesCalloutContent from "../pages/tasks/ViewNotesCalloutContent.js";
import AccordionWrapped from "./AccordionWrapped.js";
import Modal from "./Modal.js";
import Switch from "./Switch.js";
import {
  formatStateHistorySummary,
  getComponentServiceState
} from "./ComponentServiceStateHistory.js";
import {
  InputText,
  InputNumber,
  InputCheck,
  InputCheckGroup,
  InputRadioGroup,
  InputSelect,
  InputTextArea,
  InputSwitch,
  InputConfirm,
  InputRange,
  InputFile,
  InputGenerated,
  InputDate,
  InputTime
} from "./form/FormInputs.js";

// Style
import {
  border,
  colors,
  pad,
  radius,
  shadow,
  navHeight,
  status as statusColors
} from "../style/components/variables.js";
import {flex, z} from "../style/components/mixins.js";
import {voice} from "../style/components/typography.js";
import {bp} from "../style/components/breakpoints.js";
import {cross} from "../style/components/shapes.js";
import {
  FormGroup,
  Label,
  FormGroupHeading,
  StickyWrapper,
  Button,
  Inline,
  HeadingCenter,
  TextCenter,
  Abbr,
  Text
} from "../style/components/general.js";

const Element = ({
  room,
  stage,
  task,
  taskRecord,
  taskType = "checksheet",
  attributes,
  readOnly = false,
  children,
  previousResponses,
  weather,
  restriction,
  setFormulasEvaluating,
  globalUnacceptable,
  globalUnacceptableTitles
}) => {
  const {
    type,
    name,
    label,
    prompt,
    required,
    element,
    parentName,
    tag,
    condition,
    shared,
    linkedAcrossStates
  } = attributes;

  const {byId} = useMemo(() => task?.builder || {}, [task]);

  const previousRead = useMemo(
    () => (previousResponses && name in previousResponses && previousResponses[name]) || {},
    [name, previousResponses]
  );

  let formattedCondition;
  if (condition) {
    formattedCondition = `${condition.action}: `;
    condition.list.map(({check, compare, depends, operator, trigger}, idx) => {
      if (condition.list.length > 1 && idx === condition.list.length - 1)
        formattedCondition += `, ${condition.operator} `;
      else if (condition.list.length > 1 && idx > 0) formattedCondition += ", ";

      const checkArray = Array.isArray(check) ? check : [check];
      const compareArray = Array.isArray(compare) ? compare : [compare];

      const dependsType = byId[depends]?.type || depends;
      let dependsUnits = byId[depends]?.units ? ` ${byId[depends]?.units}` : "";
      let dependsLabel = byId[depends]?.label || depends?.toUpperCase();

      if (depends === "cumulative") dependsLabel = "CUMULATIVE RAINFALL";
      if (depends === "precipitationChance") dependsLabel = "PRECIPITATION CHANCE";
      if (depends === "cumulative" || depends === "rainfall") dependsUnits = " inches";
      if (depends === "precipitationChance") dependsUnits = "%";

      if (depends === "weekday" || depends === "date") {
        compareArray.map((compareVal, compareIdx) => {
          if (depends === "date") {
            let transformed = "";
            const parts = compareVal.split("_");
            if (parts?.length > 1) {
              transformed = parts[1].toUpperCase();
              transformed +=
                parts[1] === "monthly" && parts?.length === 3
                  ? " on the last day of the month"
                  : ` starting ${dayjs(parts[0].slice(5)).format("MMM D")}`;
            } else transformed = dayjs(compare.slice(5)).format("MMM D");
            formattedCondition += transformed;
          } else formattedCondition += compareVal;
          if (compareIdx < compareArray.length - 2) formattedCondition += ", ";
          if (compareIdx === compareArray.length - 2) formattedCondition += ` ${operator} `;
        });
      } else {
        formattedCondition += `${dependsLabel} `;

        checkArray.map((checkVal, checkIdx) => {
          let comp = compareArray[checkIdx];

          // "parentDisabled" is a Component Service State condition trigger — condition depends on whether the parent component is in/out of service,
          if (trigger === "parentDisabled") {
            comp =
              comp === "disabled"
                ? byId[depends]?.disabledLabel || "Out of Service"
                : byId[depends]?.enabledLabel || "In Service";
          }

          let ch = checkVal || "is";

          if (ch === "===") ch = "is";
          if (ch === "!==") ch = "is not";
          if (ch === ">=") ch = "≥";
          if (ch === "<=") ch = "≤";
          if (ch === ">" && dependsType === "time") ch = "is after";
          if (ch === "<" && dependsType === "time") ch = "is before";
          if (dependsType === "multiselect") {
            ch = "has";
            if (comp === "checked") comp = "all checked";
            if (comp === "unchecked") comp = "all unchecked";
            if (comp === "one") comp = "one checked";
            if (comp === "more") comp = "at least one checked";
          }

          if (typeof comp === "string" && comp?.match(/^Other: /))
            comp = comp.replace(/^Other: /, "");

          formattedCondition += `${ch} ${comp}${dependsUnits}`;
          if (checkIdx < checkArray.length - 1) formattedCondition += ` ${operator} `;
        });
      }
    });
  }

  if ((!parentName && element === GROUP) || element === COMPONENT) return children;

  if (type === "checkbox") {
    const {hasAlert, alertMessage, alertCondition} = attributes;
    return (
      <>
        <InputCheck
          name={name}
          label={label}
          disabled={readOnly || !!restriction}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          hasAlert={hasAlert}
          alertMessage={alertMessage}
          alertCondition={alertCondition === "checked"}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}>
          {prompt}
        </InputCheck>
        {children}
      </>
    );
  }

  if (type === "multiselect") {
    const {options, all} = attributes;
    return (
      <>
        <InputCheckGroup
          name={name}
          label={label}
          prompt={prompt}
          options={options}
          all={all}
          disabled={readOnly || !!restriction}
          tag={tag}
          condition={formattedCondition}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "radio") {
    const {options, other, orient, hasAlert, alertMessage, alertCondition} = attributes;
    return (
      <>
        <InputRadioGroup
          name={name}
          showError
          label={label}
          prompt={prompt}
          other={other}
          options={options}
          disabled={readOnly || !!restriction}
          orient={orient}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          hasAlert={hasAlert}
          alertMessage={alertMessage}
          alertCondition={alertCondition}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "dropdown") {
    const {options, placeholder, other, hasAlert, alertMessage, alertCondition} = attributes;
    return (
      <>
        <InputSelect
          name={name}
          label={label}
          placeholder={placeholder ?? DEFAULT_DROPDOWN_PLACEHOLDER}
          prompt={prompt}
          options={options}
          disabled={readOnly || !!restriction}
          other={other}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          hasAlert={hasAlert}
          alertMessage={alertMessage}
          alertCondition={alertCondition}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "textarea") {
    const {maxLength} = attributes;
    return (
      <>
        <InputTextArea
          name={name}
          label={label}
          prompt={prompt}
          disabled={readOnly || !!restriction}
          maxLength={maxLength}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "switch") {
    const {on, off, hasAlert, alertMessage, alertCondition} = attributes;
    return (
      <>
        <InputSwitch
          name={name}
          label={label}
          prompt={prompt}
          disabled={readOnly || !!restriction}
          on={on}
          off={off}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          hasAlert={hasAlert}
          alertMessage={alertMessage}
          alertCondition={alertCondition === on}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
        />
        {children}
      </>
    );
  }

  if (type === "confirm") {
    const {phone, email} = attributes;
    return (
      <>
        <InputConfirm
          name={name}
          label={label}
          prompt={prompt}
          disabled={readOnly || !!restriction}
          phone={phone}
          email={email}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required
        />
        {children}
      </>
    );
  }

  if (type === "weather") return null;

  if (type === "range") {
    const {
      rangeMin,
      rangeMax,
      precision,
      aMin,
      aMax,
      units,
      degree,
      hasARange,
      rangeException,
      hasSetPoint,
      setLow,
      setHigh,
      hasQualifier,
      notification,
      hasInfinity
    } = attributes;

    let precisionNumber = precision;
    if (exists(precisionNumber) && typeof precisionNumber === "string")
      precisionNumber = parseInt(precisionNumber, 10);

    return (
      <>
        <InputRange
          name={name}
          label={label}
          prompt={prompt}
          degree={degree}
          precision={precisionNumber}
          disabled={readOnly || !!restriction}
          rangeMin={rangeMin}
          rangeMax={rangeMax}
          aMin={aMin}
          aMax={aMax}
          units={units}
          hasARange={hasARange}
          rangeException={rangeException}
          hasSetPoint={hasSetPoint}
          setLow={setLow}
          setHigh={setHigh}
          previousRead={previousRead}
          qualifier={hasQualifier}
          notification={notification}
          allowInfinite={hasInfinity}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "time") {
    const {military} = attributes;
    return (
      <>
        <InputTime
          name={name}
          label={label}
          prompt={prompt}
          tag={tag}
          condition={formattedCondition}
          military={military}
          disabled={readOnly || !!restriction}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (type === "number") {
    const {
      min,
      max,
      aMin,
      aMax,
      strictMin,
      strictMax,
      units,
      degree,
      precision,
      hasARange,
      rangeException,
      restrictedRangeException,
      hasSetPoint,
      setLow,
      setHigh,
      hasQualifier,
      notification,
      hasInfinity
    } = attributes;

    let precisionNumber = precision;
    if (exists(precisionNumber) && typeof precisionNumber === "string")
      precisionNumber = parseInt(precisionNumber, 10);

    return (
      <>
        <InputNumber
          name={name}
          label={label}
          prompt={prompt}
          degree={degree}
          precision={precisionNumber}
          disabled={readOnly || !!restriction}
          min={min}
          max={max}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          aMin={aMin}
          aMax={aMax}
          strictMin={strictMin}
          strictMax={strictMax}
          units={units}
          hasARange={hasARange}
          rangeException={rangeException}
          restrictedRangeException={restrictedRangeException}
          hasSetPoint={hasSetPoint}
          setLow={setLow}
          setHigh={setHigh}
          previousRead={previousRead}
          qualifier={hasQualifier}
          notification={notification}
          allowInfinite={hasInfinity}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
        />
        {children}
      </>
    );
  }

  if (attributes.type === "generated") {
    const {
      units,
      depends,
      trueDepends,
      formula,
      trueFormula,
      disabled,
      precision,
      aMin,
      aMax,
      min,
      max,
      strictMin,
      strictMax,
      hasARange,
      hasSetPoint,
      setLow,
      setHigh,
      rangeException,
      restrictedRangeException,
      formulaString,
      hasQualifier,
      hasDivideByZeroDefault,
      divideByZeroDefault,
      absoluteValue,
      hidden,
      alertStartAfterStop,
      startAfterStopException
    } = attributes;
    // handle both string and array depends field
    const temp = trueDepends ?? depends;
    const trueDependsList = Array.isArray(temp) ? temp : [temp];
    const dependsList = Array.isArray(depends) ? depends : [depends];

    let precisionNumber = precision;
    if (exists(precisionNumber) && typeof precisionNumber === "string")
      precisionNumber = parseInt(precisionNumber, 10);

    return (
      <>
        <InputGenerated
          stateId={task.state && task.stateActive ? task.state.id : undefined}
          room={room}
          name={name}
          task={task}
          taskRecord={taskRecord}
          taskType={taskType}
          stage={stage}
          label={label}
          units={units}
          precision={precisionNumber}
          trueDepends={trueDependsList}
          trueFormula={trueFormula ?? formula}
          formula={formula}
          condition={formattedCondition}
          depends={dependsList}
          readOnly={readOnly || !!restriction}
          disabled={disabled}
          globalUnacceptable={globalUnacceptable}
          globalUnacceptableTitles={globalUnacceptableTitles}
          aMin={aMin}
          aMax={aMax}
          min={min}
          max={max}
          strictMin={strictMin}
          strictMax={strictMax}
          hasARange={hasARange}
          hasSetPoint={hasSetPoint}
          setLow={setLow}
          setHigh={setHigh}
          rangeException={rangeException}
          restrictedRangeException={restrictedRangeException}
          formulaString={formulaString}
          previousRead={previousRead}
          qualifier={hasQualifier}
          absoluteValue={absoluteValue}
          alertStartAfterStop={alertStartAfterStop}
          startAfterStopException={startAfterStopException}
          errorDefault={
            hasDivideByZeroDefault && exists(divideByZeroDefault) ? `${divideByZeroDefault}` : null
          }
          weather={weather}
          setFormulasEvaluating={setFormulasEvaluating}
          hidden={hidden}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
        />
        {children}
      </>
    );
  }

  if (attributes.type === "upload") {
    return (
      <>
        <InputFile
          name={name}
          label={label}
          prompt={prompt}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
          disabled={readOnly || !!restriction}
        />
        {children}
      </>
    );
  }

  if (attributes.type === "date") {
    return (
      <>
        <InputDate
          name={name}
          label={label}
          prompt={prompt}
          condition={formattedCondition}
          tag={tag}
          shared={shared}
          linkedAcrossStates={linkedAcrossStates}
          required={required}
          disabled={readOnly || !!restriction}
        />
        {children}
      </>
    );
  }

  return (
    <>
      <InputText
        name={name}
        label={label}
        prompt={prompt}
        disabled={readOnly || !!restriction}
        condition={formattedCondition}
        tag={tag}
        required={required}
      />
      {children}
    </>
  );
};

const PROP_TYPES = {
  room: PropTypes.string,
  attributes: PropTypes.objectOf(PropTypes.any).isRequired,
  children: PropTypes.node,
  task: PropTypes.objectOf(PropTypes.any),
  taskRecord: PropTypes.objectOf(PropTypes.any),
  taskType: PropTypes.string,
  stage: PropTypes.string,
  readOnly: PropTypes.bool,
  restriction: PropTypes.string,
  previousResponses: PropTypes.objectOf(PropTypes.any),
  weather: PropTypes.objectOf(PropTypes.any),
  setFormulasEvaluating: PropTypes.func,
  globalUnacceptable: PropTypes.node,
  globalUnacceptableTitles: PropTypes.arrayOf(PropTypes.string)
};

const withWrapper = CurrentElement => {
  const WrappedElement = props => {
    const {
      attributes,
      togglePreview,
      setShowModalHelp,
      setTarget,
      task,
      taskRecord,
      taskType = "checksheet",
      isOpen = false,
      setIsOpen,
      room,
      showPreview = false,
      allowNotes = true,
      restriction,
      handleDisableElement,
      onOpenStateHistory,
      simulatedDate,
      freezeComponentState = true,
      preview = false,
      children
    } = props;

    const {byId} = useMemo(() => task?.builder || {}, [task]);

    const isMounted = useMountedState();

    const {setValue, watch} = useFormContext();

    const {settings} = useContext(SettingsContext);

    const {api: apiNotes} = useApi(
      taskType === "checksheet" ? "record-notes" : "event-record-notes"
    );

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    // Note Management
    const [currentNote, setCurrentNote] = useState(null);
    const [notes, setNotes] = useState(null);
    const [calloutInWindow, setCalloutInWindow] = useState(false);

    // Only needed until the notes callout has been seen
    const scrolling = useScrollListener(undefined, {enabled: !calloutInWindow});
    const [addNoteCalloutVisible, setAddNoteCalloutVisible] = useState(false);
    const [viewNotesCalloutVisible, setViewNotesCalloutVisible] = useState(false);

    const callout = useRef();

    const compareStates = useCallback((state1, state2) => {
      if (!state1 && !state2) return undefined;
      if (!state1) return state2;
      if (!state2) return state1;
      if (state1.disabled === state2.disabled) return state1;
      return state1.time >= state2.time ? state1 : state2;
    }, []);

    const componentStateAtSubmission = useMemo(() => {
      if (!taskRecord && !simulatedDate) return undefined;

      const {stateHistory} = attributes;
      if (!stateHistory?.length) return undefined;

      let recordEpoch = new Date().getTime();
      if (taskRecord) {
        const {createdAt, updatedAt, draft} = taskRecord;
        const dateToUse = draft ? updatedAt || createdAt : createdAt;
        recordEpoch = new Date(dateToUse).getTime();
      }

      if (simulatedDate) {
        const dateInTz = dayjs.tz(simulatedDate, settings?.timezone || "US/Pacific");
        recordEpoch = dateInTz.valueOf();
      }

      // check earliest state change to avoid iterating all the way through history every time
      const earliest = stateHistory[stateHistory.length - 1];
      // if earliest state change occurred after the record, we assume the component was not disabled
      if (earliest.time > recordEpoch) return undefined;

      // iterate over stateHistory, stop when time in history entry is less than record epoch
      let i = 0;
      while (i < stateHistory.length && stateHistory[i].time >= recordEpoch) i++;

      // 'i' will be the index corresponding to the most recent state change at the time
      // of record completion
      return i === stateHistory.length ? undefined : stateHistory[i];
    }, [attributes, taskRecord, settings, simulatedDate]);

    const disabled = useMemo(() => {
      if (taskType === "event") return undefined;

      // `${name}_state` is the in-form key for this component's Component Service State (enabled/disabled)
      const stateInTask = watch(`${attributes.name}_state`);

      let stateToUse;

      if (freezeComponentState) stateToUse = compareStates(stateInTask, componentStateAtSubmission);
      else stateToUse = compareStates(stateInTask, getComponentServiceState(attributes));

      return stateToUse?.disabled || false;
    }, [
      taskType,
      attributes,
      watch,
      freezeComponentState,
      componentStateAtSubmission,
      compareStates
    ]);

    const getNotes = useCallback(() => {
      if (attributes.element === GROUP) {
        const params = {
          taskId: task.id,
          status: "pending",
          [!task?.stages ? "group" : "stage"]: attributes.name,
          ...(task?.stateActive && task.state ? {stateId: task.state.id} : {})
        };

        apiNotes.callGet(null, params).then(({status, data}) => {
          if (status === 200) {
            const draftState = !taskRecord || taskRecord.draft;
            const filtered = data.filter(({taskRecordId, draft}) => {
              if (taskRecord) return taskRecordId === taskRecord.id && draft === draftState;
              return draft === draftState;
            });
            setNotes(filtered);
          }
        });
      }
    }, [attributes, task, taskRecord, apiNotes]);

    // Initial Load if callout is in viewport
    useEffect(() => {
      if (isMounted() && callout?.current && inViewport(callout.current) && notes === null)
        getNotes();
    }, [isMounted, notes, getNotes]);

    // Reload if callout comes into viewport on scroll
    useEffect(() => {
      if (isMounted() && calloutInWindow && notes === null) getNotes();
    }, [isMounted, calloutInWindow, notes, getNotes]);

    // Check if callout is in viewport
    useEffect(() => {
      if (isMounted() && scrolling && callout?.current && !calloutInWindow)
        setCalloutInWindow(inViewport(callout.current));
    }, [isMounted, scrolling, calloutInWindow]);

    const openEditWindow = note => {
      setCurrentNote(note);
      setViewNotesCalloutVisible(false);
      setAddNoteCalloutVisible(true);
    };

    const openDeleteModal = note => {
      setCurrentNote(note);
      setDeleteModalVisible(true);
    };

    const closeDeleteModal = () => {
      setCurrentNote(null);
      setDeleteModalVisible(false);
      getNotes();
    };

    const deleteNote = () => apiNotes.callDelete(currentNote.id).then(() => closeDeleteModal());

    const showEditor =
      togglePreview &&
      (!attributes.parentName || attributes.parentName === "") &&
      attributes.toggle;
    const groupTitleInHeader = showEditor && attributes.element === GROUP;
    const hasNoteRail =
      attributes.element === GROUP && !attributes.toggle && allowNotes;

    const element = (
      <OuterWrapper>
        <ElementWrapper
          id={attributes.element !== FIELD ? attributes.name : undefined}
          type={attributes.element}
          restriction={restriction ? 1 : 0}
          parent={attributes.parentName}
          fieldPadding={
            attributes.element === FIELD &&
            attributes.parentName &&
            byId[attributes.parentName]?.element === GROUP
          }
          data-testid={`${attributes.element}-${attributes.name}`}
          hide={attributes.hidden ? 1 : 0}
          preview={preview ? 1 : 0}
          empty={
            attributes.element === COMPONENT &&
            !children?.some?.(child => !!child) &&
            attributes.allowDisable
          }>
          {groupTitleInHeader && (
            <PreviewHeader>
              <ElementLabel type={attributes.element}>{attributes.label}</ElementLabel>
              <Button type="button" onClick={() => togglePreview(attributes.name)}>
                Editor
              </Button>
            </PreviewHeader>
          )}
          {showEditor && !groupTitleInHeader && (
            <StickyButton>
              <Button type="button" onClick={() => togglePreview(attributes.name)}>
                Editor
              </Button>
            </StickyButton>
          )}

          {attributes.element === COMPONENT ? (
            <ComponentHeader>
              <Help
                type="button"
                element={attributes.element}
                onClick={
                  setShowModalHelp
                    ? () => {
                        setShowModalHelp(true);
                        setTarget(attributes);
                      }
                    : undefined
                }
                data-testid={`${attributes.name}-help`}
                hasPointer={!!setShowModalHelp}>
                <ComponentHeading>
                  <ElementLabel type={attributes.element} restriction={restriction ? 1 : 0}>
                    {attributes.label}
                  </ElementLabel>
                  {(attributes?.help?.length > 0 ||
                    attributes?.address?.line1 ||
                    (exists(attributes?.address?.lat) && exists(attributes?.address?.lon))) &&
                    setShowModalHelp && (
                      <HelpIcon type={attributes.element} icon={faQuestionCircle} />
                    )}
                </ComponentHeading>
              </Help>
              {taskType === "checksheet" && attributes.allowDisable && (
                <ComponentControls>
                  <Switch
                    on={attributes.enabledLabel || "In Service"}
                    off={attributes.disabledLabel || "Out of Service"}
                    state={!disabled}
                    callback={
                      handleDisableElement
                        ? () => handleDisableElement(attributes.name)
                        : () => {}
                    }
                    onColor={statusColors.success}
                    offColor={statusColors.error}
                    syncState
                    disabled={!!freezeComponentState || !handleDisableElement}
                  />
                  {attributes.stateHistory?.length > 0 && onOpenStateHistory && (
                    <StateHistoryButton
                      type="button"
                      title="View full Component Service State history"
                      onClick={() => onOpenStateHistory(attributes.name)}>
                      {formatStateHistorySummary(attributes.stateHistory[0], {
                        enabledLabel: attributes.enabledLabel,
                        disabledLabel: attributes.disabledLabel,
                        timezone: settings?.timezone
                      })}
                    </StateHistoryButton>
                  )}
                </ComponentControls>
              )}
            </ComponentHeader>
          ) : (
            <Inline>
              {attributes.element === GROUP && attributes.toggle && !groupTitleInHeader && (
                <ElementLabel type={attributes.element}>{attributes.label}</ElementLabel>
              )}
            </Inline>
          )}

          {attributes.globalPrompt && (
            <GlobalPromptLabel bold>{attributes.globalPrompt}</GlobalPromptLabel>
          )}

          <Content
            type={attributes.element}
            $hasNoteRail={hasNoteRail}
            scaleDown={room === "tasks"}
            grid={attributes.grid}
            showPreview={showPreview}>
            {hasNoteRail ? (
              <GroupContentMain>
                <CurrentElement {...props} />
              </GroupContentMain>
            ) : (
              <CurrentElement {...props} />
            )}

            {attributes.element === "group" && !attributes.toggle && (
              <>
                {deleteModalVisible && (
                  <Modal visible={deleteModalVisible} setVisible={setDeleteModalVisible}>
                    <HeadingCenter>Confirm Delete</HeadingCenter>
                    <FormGroup>
                      <TextCenter>
                        Are you sure you want to delete? This action cannot be undone.
                      </TextCenter>
                    </FormGroup>
                    <Delete type="button" onClick={() => deleteNote()}>
                      Delete
                    </Delete>
                  </Modal>
                )}

                {allowNotes && (
                  <CalloutMenu
                    calloutList={[
                      {
                        visible: addNoteCalloutVisible,
                        setVisible: state => {
                          if (!state) setCurrentNote(null);
                          setAddNoteCalloutVisible(state);
                          setValue("note", {});
                        },
                        content: (
                          <AddNoteCalloutContent
                            visible={addNoteCalloutVisible}
                            setVisible={state => {
                              if (!state) setCurrentNote(null);
                              setAddNoteCalloutVisible(state);
                              setValue("note", {});
                            }}
                            setRefreshNotes={getNotes}
                            current={currentNote}
                            setCurrent={setCurrentNote}
                            group={attributes.name}
                            label={attributes.label}
                            task={task}
                            taskRecord={taskRecord}
                            taskType={taskType}
                          />
                        ),
                        button: (
                          <AddNoteButton ref={callout} type="button" data-testid="addNote">
                            <IconWrapper rotated={!addNoteCalloutVisible}>
                              <NoteHookIcon />
                            </IconWrapper>
                          </AddNoteButton>
                        ),
                        buttonType: "circle"
                      },
                      {
                        visible: viewNotesCalloutVisible,
                        setVisible: setViewNotesCalloutVisible,
                        content: (
                          <ViewNotesCalloutContent
                            setVisible={setViewNotesCalloutVisible}
                            visible={viewNotesCalloutVisible}
                            task={task}
                            taskType={taskType}
                            groupLabel={attributes.label}
                            groupName={attributes.name}
                            notes={notes}
                            handleEdit={openEditWindow}
                            handleDelete={openDeleteModal}
                          />
                        ),
                        button: (notes?.length > 0 || task?.hasNoteHistory) && (
                          <ViewNotesButton type="button">
                            <Abbr title={notes?.length > 0 ? "Pending" : "History"}>
                              <FontAwesomeIcon icon={notes?.length > 0 ? faStickyNote : faBook} />
                            </Abbr>
                          </ViewNotesButton>
                        )
                      }
                    ]}
                  />
                )}
              </>
            )}
          </Content>
        </ElementWrapper>
      </OuterWrapper>
    );

    if (attributes.element === GROUP && !attributes.toggle)
      return isOpen !== undefined && setIsOpen ? (
        <OuterWrapper>
          <AccordionWrapped
            label={attributes.label}
            isGroup
            isEvent={taskType === "event"}
            handleOpen={() => setIsOpen(attributes.name)}
            toggleOpen={isOpen}
            disallowOpen={taskType === "event"}
            restricted={!!restriction}
            name={attributes.name}
            showHelp={
              attributes?.help?.length > 0 ||
              attributes?.address?.line1 ||
              (exists(attributes?.address?.lat) && exists(attributes?.address?.lon))
                ? () => {
                    setShowModalHelp(true);
                    setTarget(attributes);
                  }
                : null
            }>
            {element}
          </AccordionWrapped>
          {restriction && (
            <Absolute>
              <Text>{restriction.toUpperCase()} RESTRICTION APPLIED</Text>
            </Absolute>
          )}
        </OuterWrapper>
      ) : null;

    return element;
  };

  WrappedElement.propTypes = {
    ...PROP_TYPES,
    setTarget: PropTypes.func,
    togglePreview: PropTypes.func,
    setShowModalHelp: PropTypes.func,
    conditionsSatisfied: PropTypes.objectOf(PropTypes.any),
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    preview: PropTypes.bool,
    showPreview: PropTypes.bool,
    allowNotes: PropTypes.bool,
    handleDisableElement: PropTypes.func,
    onOpenStateHistory: PropTypes.func,
    freezeComponentState: PropTypes.bool,
    simulatedDate: PropTypes.string
  };

  return WrappedElement;
};

Element.propTypes = PROP_TYPES;

// Style Overrides
const ElementWrapper = styled(FormGroup)`
  width: 100%;
  border-radius: ${radius};
  padding: ${pad}px ${pad / 2}px;
  padding-top: 0;
  height: min-content;
  flex-wrap: nowrap;

  ${({empty}) =>
    empty
      ? css`
          opacity: 0.5;
        `
      : ""}

  ${({restriction, parent, type}) =>
    restriction && !parent && type !== GROUP
      ? css`
          opacity: 0.5;
        `
      : ""}

  ${({type, theme, preview}) =>
    type === GROUP &&
    css`
      border: ${preview ? `${border} solid ${theme.group}` : 0};
      border-radius: ${radius};
      color: ${theme.group};
      background: transparent;
      padding: ${pad}px;
    `}

  ${({type, theme}) =>
    type === COMPONENT &&
    css`
      border: 0;
      border-left: 3px solid ${theme.component};
      border-radius: ${radius};
      color: ${theme.component};
      background: ${colors.heroWhite};
      padding: ${pad}px;
      margin: ${pad / 2}px 0;
    `}

  ${({type}) =>
    type === FIELD &&
    css`
      ${flex("column", "nowrap", "start", "start")};
      position: relative;
      background: transparent;
      min-width: 200px;
      padding: 0;
    `}

  ${({fieldPadding}) =>
    fieldPadding &&
    css`
      padding: 0 ${pad / 2}px;
    `}

  ${({hide}) =>
    hide &&
    css`
      display: none;
    `}
`;

const GlobalPromptLabel = styled(Label)`
  && {
    padding-left: ${pad / 2}px;
    white-space: normal;
    align-self: stretch;
    max-width: 100%;
  }
`;

const ElementLabel = styled(FormGroupHeading)`
  color: ${({theme}) => theme.group};
  text-transform: uppercase;
  margin-bottom: ${pad / 2}px;

  ${({type, theme}) =>
    type === GROUP &&
    css`
      ${voice.medium};
      font-weight: bold;
      color: ${theme.group};
      margin-bottom: 0;
      line-height: 1.2;
    `}

  ${({type, theme}) =>
    type === COMPONENT &&
    css`
      ${voice.medium};
      font-weight: bold;
      color: ${theme.component};
      margin-bottom: 0;
      line-height: 1.2;
    `}
`;

const GroupContentMain = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const Content = styled.div.withConfig({
  shouldForwardProp: prop => !["$hasNoteRail", "scaleDown", "showPreview", "grid"].includes(prop)
})`
  width: 100%;

  ${({type, $hasNoteRail}) =>
    type === GROUP &&
    css`
      ${$hasNoteRail
        ? css`
            ${flex("row", "nowrap", "flex-start", "stretch")};
            gap: ${pad / 4}px;
          `
        : css`
            ${flex("column", "nowrap", "flex-start", "stretch")};
          `}
    `}

  ${({type}) =>
    type === "field" &&
    css`
      max-width: calc(100% - 50px);
    `}

  ${({showPreview}) =>
    showPreview &&
    css`
      opacity: 0.35;
    `}

  ${({grid, scaleDown}) =>
    grid &&
    css`
      ${flex("row", "wrap", "flex-start", "flex-start")};

      h2 {
        width: 100%;
      }

      > div:not([data-callout-rail]) {
        padding-right: ${pad}px;
        max-width: 100%;

        ${bp(scaleDown ? 3 : 4)} {
          max-width: 50%;
        }

        ${bp(scaleDown ? 4 : 5)} {
          max-width: 33.33%;
        }
      }
    `}
`;

const StickyButton = styled(StickyWrapper)`
  top: ${navHeight + pad * 2}px;
  margin-top: ${pad}px;
  margin-bottom: 0;
  display: flex;
  justify-content: flex-end;
  z-index: ${z("above")};
  width: 100%;

  button {
    box-shadow: ${shadow};
  }
`;

const PreviewHeader = styled(StickyWrapper)`
  position: relative;
  top: auto;
  margin-top: 0;
  margin-bottom: ${pad / 2}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pad}px;
  z-index: auto;
  width: 100%;

  h2 {
    margin-bottom: 0;
  }

  button {
    box-shadow: ${shadow};
  }
`;

const Help = styled.button`
  width: auto;
  padding: ${pad / 2}px 0;
  display: inline-flex;
  align-items: center;

  ${({hasPointer}) => css`
    cursor: ${hasPointer ? "pointer" : "default"};
  `}

  ${({element, theme}) =>
    element === GROUP &&
    css`
      fill: ${theme.group};
    `}

  ${({element, theme}) =>
    element === COMPONENT &&
    css`
      fill: ${theme.component};
      padding: 0;
    `}
`;

const HelpIcon = styled(FontAwesomeIcon)`
  margin: 0;
`;

const AddNoteButton = styled(Button)`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  max-width: none;
`;

const ViewNotesButton = styled(Button)`
  font-size: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  max-width: none;
  background-color: ${({theme}) => theme.primary};
`;

const StateHistoryButton = styled.button`
  ${voice.quiet};
  padding: 0;
  max-width: min(100%, 420px);
  text-align: left;
  color: ${({theme}) => theme.secondary};
  text-decoration: underline;
  cursor: pointer;
  line-height: 1.2;

  &:hover {
    color: ${({theme}) => theme.primary};
  }
`;

const NoteHookIcon = styled.div`
  ${({theme}) => cross("100%", theme.tertiary, "2.5px")}
`;

const IconWrapper = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  right: 10px;
  top: 10px;
  transform: ${({rotated}) => (rotated ? css`rotate(45deg)` : css`rotate(0deg)`)};
  transform-origin: center center;
  transition: transform 0.2s;
`;

const Delete = styled(Button)`
  background: ${({theme}) => theme.error};
  color: ${({theme}) => theme.tertiary};
`;

const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  margin-bottom: ${pad}px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Absolute = styled.div`
  position: absolute;
  margin: auto;
  align-self: center;
  padding: ${pad}px;
  border-radius: ${radius};
  text-shadow: ${shadow};

  p {
    ${voice.medium};
    font-weight: bold;
  }
`;

const ComponentHeader = styled(Inline)`
  align-items: center;
  flex-wrap: wrap;
  gap: ${pad}px;
  width: 100%;
`;

const ComponentControls = styled(Inline)`
  align-items: center;
  gap: ${pad / 2}px;
`;

const ComponentHeading = styled(Inline)`
  width: max-content;
  align-items: center;
  gap: ${pad / 2}px;
`;

const WrappedElement = withWrapper(Element);

export default WrappedElement;
